import { defineStore } from 'pinia'
import type { User } from '@/models/User'
import { authService } from '@/services/auth.service'

interface AuthState {
    user: User | null
    token: string | null
    isAuthenticated: boolean
    impersonating: boolean
    originalUser: User | null
    originalToken: string | null
}

export const useAuthStore = defineStore('auth', {
    state: (): AuthState => ({
        user: null,
        token: localStorage.getItem('user_token'),
        isAuthenticated: !!localStorage.getItem('user_token'),
        impersonating: false,
        originalUser: null,
        originalToken: null
    }),

    getters: {
        getUser: (state) => state.user,
        getToken: (state) => state.token,
        isLoggedIn: (state) => state.isAuthenticated,
        isImpersonating: (state) => state.impersonating
    },

    actions: {
        async login(email: string, password: string) {
            try {
                console.log('Tentative de connexion pour:', email)
                const userData = await authService.login({ email, password })

                if (userData) {
                    console.log('Réponse de connexion réussie:', userData)
                    this.user = userData
                    this.token = userData.authentication_token
                    this.isAuthenticated = true
                    localStorage.setItem('user_token', userData.authentication_token)
                    localStorage.setItem('user_email', userData.email)
                    console.log('État du store après connexion:', {
                        user: this.user,
                        token: this.token,
                        isAuthenticated: this.isAuthenticated
                    })
                    console.log('Features après connexion:', this.user.features)
                    return true
                }
                console.log('Échec de la connexion: pas de données utilisateur')
                return false
            } catch (error) {
                console.error('Erreur de connexion:', error)
                this.logout()
                return false
            }
        },

        async register(name: string, email: string, password: string) {
            try {
                const result = await authService.register({ name, email, password })

                if (result.user) {
                    this.user = result.user
                    this.token = result.user.authentication_token
                    this.isAuthenticated = true
                    localStorage.setItem('user_token', result.user.authentication_token)
                    localStorage.setItem('user_email', result.user.email)
                    console.log('Features après inscription:', this.user.features)
                    return { success: true, errors: null }
                }
                return { success: false, errors: result.errors }
            } catch (error) {
                console.error('Erreur d\'inscription:', error)
                this.logout()
                return { success: false, errors: null }
            }
        },

        logout() {
            console.log('Déconnexion - État avant:', {
                user: this.user,
                token: this.token,
                isAuthenticated: this.isAuthenticated
            })
            this.user = null
            this.token = null
            this.isAuthenticated = false
            localStorage.removeItem('user_token')
            localStorage.removeItem('user_email')
            console.log('Déconnexion - État après:', {
                user: this.user,
                token: this.token,
                isAuthenticated: this.isAuthenticated
            })
        },
        async checkAuth() {
            const token = localStorage.getItem('user_token')
            const email = localStorage.getItem('user_email')
            console.log('Vérification de l\'authentification:', {
                token,
                email,
                currentUser: this.user,
                isAuthenticated: this.isAuthenticated
            })

            // Si pas de token ou d'email, déconnexion immédiate
            if (!token || !email) {
                console.log('Pas de token ou email trouvé, déconnexion')
                this.logout()
                return false
            }

            // Si l'utilisateur est déjà chargé et authentifié, vérifier le token
            if (this.user && this.isAuthenticated) {
                if (this.user.authentication_token !== token) {
                    console.log('Token incohérent, déconnexion')
                    this.logout()
                    return false
                }
                return true
            }

            // Mettre à jour l'état du store avec les données du localStorage
            this.token = token
            this.isAuthenticated = true

            // Créer un objet utilisateur minimal
            this.user = {
                id: 0,
                email: email,
                authentication_token: token
            }

            // Vérifier l'authentification avec le serveur
            try {
                const userData = await authService.checkAuth()
                if (userData) {
                    this.user = userData
                    console.log('Informations utilisateur récupérées:', this.user)
                    console.log('Features de l\'utilisateur:', this.user.features)
                    return true
                } else {
                    console.log('Échec de la récupération des informations utilisateur')
                    this.logout()
                    return false
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des informations utilisateur:', error)
                this.logout()
                return false
            }
        },

        async impersonateUser(userId: number) {
            try {
                const response = await authService.impersonate(userId)
                if (response) {
                    // Sauvegarder l'état original
                    this.originalUser = this.user
                    this.originalToken = this.token

                    // Mettre à jour avec les nouvelles informations
                    this.user = response
                    this.token = response.authentication_token
                    this.isAuthenticated = true
                    this.impersonating = true

                    // Mettre à jour le localStorage
                    localStorage.setItem('user_token', response.authentication_token)
                    localStorage.setItem('user_email', response.email)
                    localStorage.setItem('impersonating', 'true')
                    localStorage.setItem('original_token', this.originalToken || '')
                    localStorage.setItem('original_email', this.originalUser?.email || '')

                    console.log('Features après impersonation:', this.user.features)
                    return true
                }
                return false
            } catch (error) {
                console.error('Erreur lors de l\'impersonation:', error)
                return false
            }
        },

        async stopImpersonating() {
            if (!this.originalUser || !this.originalToken) return false

            try {
                console.log('Arrêt de l\'impersonation - État avant:', {
                    user: this.user,
                    token: this.token,
                    isAuthenticated: this.isAuthenticated,
                    impersonating: this.impersonating
                })
                // Restaurer l'état original
                this.user = this.originalUser
                this.token = this.originalToken
                this.isAuthenticated = true
                this.impersonating = false

                // Mettre à jour le localStorage
                localStorage.setItem('user_token', this.originalToken)
                localStorage.setItem('user_email', this.originalUser.email)
                localStorage.removeItem('impersonating')
                localStorage.removeItem('original_token')
                localStorage.removeItem('original_email')

                // Nettoyer l'état d'impersonation
                this.originalUser = null
                this.originalToken = null

                return true
            } catch (error) {
                console.error('Erreur lors de l\'arrêt de l\'impersonation:', error)
                return false
            }
        },

        async forgotPassword(email: string) {
            try {
                await authService.forgotPassword(email)
                return true
            } catch (error) {
                console.error('Erreur lors de la demande de réinitialisation:', error)
                throw error
            }
        }
    }
}) 